/**
 * RatingsLog — AI 사진 별점 개별 평가 로그 (AIQualityRatings 의 '평가 로그' 탭)
 *
 * GET /api/admin/ratings/log?page=&pageSize=&from=&to=&ratedFrom= 호출.
 * "누가 / 어떤 사진에 / 몇 점 / 어떤 컨셉 / 어디서 / 언제" 평가했는지 최신순으로 보여준다.
 */

import { useEffect, useMemo, useState } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { Spinner, Select, Pagination, Badge, formatDate } from '@/ui'
import { buildRangeQs, type Range } from './AIQualityRatings'
import styles from './RatingsLog.module.css'

type RatedFromFilter = 'all' | 'generate' | 'generate_exit' | 'album'

interface LogRow {
  id: string
  userName: string | null
  userEmail: string
  imageUrl: string
  rating: number
  ratedFrom: string
  conceptSlug: string | null
  conceptNameKo: string | null
  createdAt: string
  updatedAt: string
}

interface LogResponse {
  rows: LogRow[]
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

const PAGE_SIZE = 30

const SENTIMENT_LABEL: Record<number, { label: string; emoji: string; color: 'gray' | 'green' | 'yellow' | 'red' }> = {
  3: { label: '최고에요', emoji: '😍', color: 'green' },
  2: { label: '좋아요', emoji: '😊', color: 'yellow' },
  1: { label: '별로에요', emoji: '😕', color: 'red' },
}

const RATED_FROM_LABEL: Record<string, string> = {
  generate: '생성 화면',
  generate_exit: '생성 후 평가',
  album: '앨범',
}

const RATED_FROM_COLOR: Record<string, 'gray' | 'blue' | 'green' | 'red' | 'yellow'> = {
  generate: 'blue',
  generate_exit: 'green',
  album: 'gray',
}

export function RatingsLog({ range }: { range: Range }) {
  const [page, setPage] = useState(1)
  const [ratedFrom, setRatedFrom] = useState<RatedFromFilter>('all')

  // 기간·출처 필터가 바뀌면 1페이지로 되돌린다 (잘못된 offset 방지)
  useEffect(() => {
    setPage(1)
  }, [range, ratedFrom])

  const path = useMemo(() => {
    const rf = ratedFrom !== 'all' ? `&ratedFrom=${ratedFrom}` : ''
    return `/api/admin/ratings/log?page=${page}&pageSize=${PAGE_SIZE}${rf}${buildRangeQs(range)}`
  }, [page, ratedFrom, range])

  const { data, isLoading, error } = useApiFetch<LogResponse>(path)

  return (
    <div className={styles.wrap}>
      <div className={styles.filterRow}>
        <Select
          label="출처"
          value={ratedFrom}
          onChange={(e) => setRatedFrom(e.target.value as RatedFromFilter)}
          options={[
            { value: 'all', label: '전체' },
            { value: 'generate', label: '생성 화면' },
            { value: 'generate_exit', label: '생성 후 평가' },
            { value: 'album', label: '앨범' },
          ]}
        />
        {data && <span className={styles.totalLabel}>총 {data.pagination.total}건</span>}
      </div>

      {isLoading ? (
        <div className={styles.loading}>
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className={styles.errorRow}>로그 조회 실패: {String(error)}</div>
      ) : !data || data.rows.length === 0 ? (
        <div className={styles.emptyRow}>해당 조건의 평가가 없습니다.</div>
      ) : (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.thumbCol}>사진</th>
                  <th>유저</th>
                  <th className={styles.scoreCol}>평가</th>
                  <th>컨셉</th>
                  <th>출처</th>
                  <th>평가 시각</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <img src={r.imageUrl} alt="" className={styles.thumb} loading="lazy" />
                    </td>
                    <td>
                      <div className={styles.userCell}>
                        <span className={styles.userName}>{r.userName ?? '(이름 없음)'}</span>
                        <span className={styles.userEmail}>{r.userEmail}</span>
                      </div>
                    </td>
                    <td>
                      {SENTIMENT_LABEL[r.rating] ? (
                        <Badge color={SENTIMENT_LABEL[r.rating].color}>
                          {SENTIMENT_LABEL[r.rating].emoji} {SENTIMENT_LABEL[r.rating].label}
                        </Badge>
                      ) : (
                        <Badge color="gray">{r.rating}</Badge>
                      )}
                    </td>
                    <td>{r.conceptNameKo ?? r.conceptSlug ?? '-'}</td>
                    <td>
                      <Badge color={RATED_FROM_COLOR[r.ratedFrom] ?? 'gray'}>
                        {RATED_FROM_LABEL[r.ratedFrom] ?? r.ratedFrom}
                      </Badge>
                    </td>
                    <td className={styles.timeCell}>
                      {formatDate(r.updatedAt, 'yyyy-MM-dd HH:mm')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.paginationRow}>
            <Pagination current={page} total={data.pagination.totalPages} onChange={setPage} />
          </div>
        </>
      )}
    </div>
  )
}
